import { useEffect, useState } from 'react'
import { api } from '../servizi/api.ts'
import { Scelta } from './base.tsx'

/**
 * Stanze di una sede (558) nei moduli che fissano un impegno: mediazione, appuntamento, impegno
 * dell'agenda. La stanza occupata blocca (decisione 6): qui si sceglie e basta, il controllo lo fa l'API
 * al salvataggio. Le stanze adatte ai colloqui stanno in cima quando servono a un colloquio.
 */

export type Stanza = {
  id: string; nome: string; tipo: 'stanza' | 'sala' | 'postazione' | 'attrezzatura'; capienza: number | null
  accessibile: boolean; adatta_colloqui: boolean; prenotabile: boolean; nota: string | null; attiva: boolean
  ordinamento: number
}

export const NOMI_TIPO_STANZA: Record<Stanza['tipo'], string> = {
  stanza: 'Stanza', sala: 'Sala', postazione: 'Postazione', attrezzatura: 'Attrezzatura',
}

/** Le stanze in uso della sede; vuoto senza sede. */
export function useStanze(sedeId: string | null | undefined) {
  const [stanze, setStanze] = useState<Stanza[]>([])
  useEffect(() => {
    if (!sedeId) { setStanze([]); return }
    let vivo = true
    api.get<{ stanze: Stanza[] }>(`/sedi/${sedeId}/stanze`)
      .then((r) => { if (vivo) setStanze(r.stanze.filter((s) => s.attiva)) })
      .catch(() => { if (vivo) setStanze([]) })
    return () => { vivo = false }
  }, [sedeId])
  return stanze
}

/** «Sala 1 · 4 posti · accessibile». */
export const etichettaStanza = (s: Stanza) => [s.nome, s.capienza ? `${s.capienza} posti` : null,
  s.accessibile ? 'accessibile' : null].filter(Boolean).join(' · ')

/** La tendina della stanza: niente se la sede non ne ha. */
export function SceltaStanza({ sedeId, valore, cambia, colloqui = false, soloPrenotabili = false, etichetta = 'Stanza', aiuto, obbligatorio }: {
  sedeId: string | null | undefined; valore: string; cambia: (v: string) => void
  /** Per colloqui con le persone: in cima le stanze adatte. */
  colloqui?: boolean
  soloPrenotabili?: boolean
  etichetta?: string
  aiuto?: string
  obbligatorio?: boolean
}) {
  const stanze = useStanze(sedeId).filter((s) => !soloPrenotabili || s.prenotabile)
  if (!sedeId || !stanze.length) return null
  const ordinate = colloqui
    ? [...stanze].sort((a, b) => Number(b.adatta_colloqui) - Number(a.adatta_colloqui))
    : stanze
  return (
    <Scelta etichetta={etichetta} valore={valore} cambia={cambia} obbligatorio={obbligatorio}
            vuota={obbligatorio ? '— scegli —' : '— nessuna stanza —'}
            opzioni={ordinate.map((s) => ({
              valore: s.id, etichetta: etichettaStanza(s),
              gruppo: colloqui ? (s.adatta_colloqui ? 'Adatte ai colloqui' : 'Le altre') : undefined,
            }))}
            aiuto={aiuto ?? 'Se la stanza è già occupata in quell’orario, l’impegno non si salva: scegline un’altra.'} />
  )
}
